import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import {
  EVENT_STATUS_OPTIONS,
  fromDatetimeLocalValue,
  toDatetimeLocalValue,
} from "../../lib/eventStatus";
import { fmtDashboardOccurrence } from "../../lib/dashboardFormat";
import { ReadableCard, ReadableNavButton } from "../Sticker";
import FormLabel from "../FormLabel";
import { ChoiceToggleGroup } from "../ui/FormSection";

const SCOPE_OPTIONS = [
  { k: "this_occurrence", label: "This date only" },
  { k: "all_future", label: "This and future dates" },
];

const STATUS_OPTIONS = EVENT_STATUS_OPTIONS.map((o) => ({ k: o.value, label: o.label }));

export default function EditWeekTab({
  data, eventId, tokenParam, occurrenceDate, onSaved, onNotifyAfterSave,
  demoMode = false, onDemoSave,
}) {
  const override = data.occurrence_overrides?.[occurrenceDate] || {};
  const [scope, setScope] = useState("this_occurrence");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    description_append: override.description_append || "",
    starts_at: toDatetimeLocalValue(data.starts_at),
    door_time: toDatetimeLocalValue(data.door_time),
    status: data.event_status || "confirmed",
    status_reason: data.status_reason || "",
  });

  useEffect(() => {
    const o = data.occurrence_overrides?.[occurrenceDate] || {};
    setForm({
      description_append: o.description_append || "",
      starts_at: toDatetimeLocalValue(data.starts_at),
      door_time: toDatetimeLocalValue(data.door_time),
      status: data.event_status || "confirmed",
      status_reason: data.status_reason || "",
    });
    setScope("this_occurrence");
  }, [data, occurrenceDate]);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    const payload = {
      occurrence_date: occurrenceDate,
      scope,
      status: form.status,
      status_reason: form.status_reason.trim() || null,
    };
    if (form.description_append.trim()) payload.description_append = form.description_append.trim();
    const startsAt = fromDatetimeLocalValue(form.starts_at);
    if (startsAt) payload.starts_at = startsAt;
    if (form.door_time) payload.door_time = fromDatetimeLocalValue(form.door_time);
    else if (form.door_time === "") payload.door_time = null;

    setSaving(true);
    try {
      if (demoMode) {
        onDemoSave?.(payload);
        toast.success(scope === "all_future" ? "Saved for this and future dates" : "Saved for this week");
        onSaved();
        onNotifyAfterSave?.();
        return;
      }
      await api.patch(`/events/${eventId}/amendments`, payload, {
        params: { token: tokenParam || undefined },
      });
      toast.success(scope === "all_future" ? "Saved for this and future dates" : "Saved for this week");
      onSaved();
      onNotifyAfterSave?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  const showReason = form.status === "cancelled" || form.status === "not_confirmed";

  return (
    <div className="max-w-page mx-auto px-4 mt-6">
      <ReadableCard className="p-5 sm:p-6 bg-leaders">
        <h2 className="font-ui font-semibold text-[22px] leading-tight">
          This week · {fmtDashboardOccurrence(data.starts_at)}
        </h2>
        <p className="mt-2 font-ui font-medium text-[14px] text-ink/80">
          Update time, status, or add a note for this date only — past weeks stay unchanged.
        </p>

        <div className="mt-5">
          <FormLabel as="p" className="mb-2 text-ink/60">Save changes for</FormLabel>
          <div data-testid="amendment-scope">
            <ChoiceToggleGroup
              options={SCOPE_OPTIONS}
              value={scope}
              onChange={setScope}
              testIdPrefix="amendment-scope"
            />
          </div>
        </div>

        <div className="mt-5 space-y-4">
          <label className="block">
            <FormLabel>Note for this week</FormLabel>
            <p className="font-ui font-medium text-[12px] text-ink/60 mt-0.5">Shows at the bottom of the event description.</p>
            <textarea
              className="field mt-2 min-h-[88px]"
              value={form.description_append}
              onChange={(e) => update("description_append", e.target.value)}
              placeholder="e.g. Live band tonight! Doors open 30 min early."
              data-testid="amendment-description"
            />
          </label>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <FormLabel className="block mb-2">Start date & time</FormLabel>
              <input
                type="datetime-local"
                className="field"
                value={form.starts_at}
                onChange={(e) => update("starts_at", e.target.value)}
                data-testid="amendment-starts-at"
              />
            </label>
            <label className="block">
              <FormLabel className="block mb-2">
                Door time <span className="text-ink/50 font-normal">(optional)</span>
              </FormLabel>
              <input
                type="datetime-local"
                className="field"
                value={form.door_time}
                onChange={(e) => update("door_time", e.target.value)}
                data-testid="amendment-door-time"
              />
            </label>
          </div>

          <div>
            <FormLabel as="p" className="mb-2">Event status</FormLabel>
            <div data-testid="amendment-status">
              <ChoiceToggleGroup
                options={STATUS_OPTIONS}
                value={form.status}
                onChange={(k) => update("status", k)}
                testIdPrefix="amendment-status"
              />
            </div>
          </div>

          {showReason && (
            <label className="block">
              <FormLabel className="block mb-2">Reason</FormLabel>
              <input
                className="field"
                value={form.status_reason}
                onChange={(e) => update("status_reason", e.target.value)}
                placeholder={form.status === "cancelled" ? "e.g. Venue unavailable" : "e.g. Waiting on final venue confirmation"}
                data-testid="amendment-status-reason"
              />
            </label>
          )}
        </div>

        <div className="mt-6">
          <ReadableNavButton variant="primary" onClick={save} disabled={saving} data-testid="save-amendments-btn">
            {saving ? "Saving…" : "Save changes"}
          </ReadableNavButton>
        </div>
      </ReadableCard>
    </div>
  );
}
